// SPDX-License-Identifier: AGPL-3.0-or-later

import {AppTitlebarContext} from '@app/features/app/components/layout/AppTitlebarContext';
import styles from '@app/features/app/components/layout/NativeTitlebar.module.css';
import {NativeWindowControls} from '@app/features/app/components/layout/NativeWindowControls';
import {getElectronAPI, type NativePlatform} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon, ArrowRightIcon} from '@phosphor-icons/react';
import type React from 'react';
import {useEffect, useLayoutEffect, useState} from 'react';

const STARTUP_NATIVE_TITLEBAR_ID = 'fluxer-startup-native-titlebar';
const WEB_CONTEXT_TITLEBAR_CLASS = 'context-titlebar';

const BACK_DESCRIPTOR = msg({
	message: 'Back',
	comment: 'Short label in the app layout native titlebar.',
});
const FORWARD_DESCRIPTOR = msg({
	message: 'Forward',
	comment: 'Tooltip on the forward button in the inline message hover action bar.',
});

interface NativeTitlebarProps {
	platform: NativePlatform;
	/** Native window chrome, or the web bar that only shows the current place. */
	surface?: 'native' | 'web';
}

interface HistoryAvailability {
	back: boolean;
	forward: boolean;
}

function readHistoryAvailability(): HistoryAvailability {
	if (typeof window === 'undefined') return {back: false, forward: false};
	const navigation = Reflect.get(window, 'navigation') as {canGoBack?: unknown; canGoForward?: unknown} | undefined;
	return {
		back: typeof navigation?.canGoBack === 'boolean' ? navigation.canGoBack : window.history.length > 1,
		forward: typeof navigation?.canGoForward === 'boolean' ? navigation.canGoForward : false,
	};
}

function useHistoryAvailability(): HistoryAvailability {
	const [availability, setAvailability] = useState<HistoryAvailability>(readHistoryAvailability);
	useEffect(() => {
		const sync = () => setAvailability(readHistoryAvailability());
		sync();
		window.addEventListener('popstate', sync);
		const navigation = Reflect.get(window, 'navigation') as
			| {addEventListener?: (type: string, listener: () => void) => void; removeEventListener?: (type: string, listener: () => void) => void}
			| undefined;
		navigation?.addEventListener?.('currententrychange', sync);
		return () => {
			window.removeEventListener('popstate', sync);
			navigation?.removeEventListener?.('currententrychange', sync);
		};
	}, []);
	return availability;
}

export const NativeTitlebar: React.FC<NativeTitlebarProps> = ({platform, surface = 'native'}) => {
	const {i18n} = useLingui();
	const isMacOS = platform === 'macos';
	const isWeb = surface === 'web';
	const history = useHistoryAvailability();
	useLayoutEffect(() => {
		const startupTitlebar = document.getElementById(STARTUP_NATIVE_TITLEBAR_ID);
		if (startupTitlebar != null) {
			startupTitlebar.remove();
		}
	}, []);
	useLayoutEffect(() => {
		if (!isWeb) return;
		document.documentElement.classList.add(WEB_CONTEXT_TITLEBAR_CLASS);
		return () => {
			document.documentElement.classList.remove(WEB_CONTEXT_TITLEBAR_CLASS);
		};
	}, [isWeb]);
	const handleDoubleClick = () => {
		if (isWeb) return;
		const electronApi = getElectronAPI();
		if (!electronApi?.windowMaximize) return;
		electronApi.windowMaximize();
	};
	return (
		<div
			role="group"
			className={styles.titlebar}
			onDoubleClick={isMacOS || isWeb ? undefined : handleDoubleClick}
			data-platform={platform}
			data-surface={surface}
			data-native-titlebar=""
			data-flx="app.native-titlebar.titlebar"
		>
			{!isWeb ? (
				<div className={styles.history} data-flx="app.native-titlebar.history">
					<button
						type="button"
						className={styles.historyButton}
						aria-label={i18n._(BACK_DESCRIPTOR)}
						disabled={!history.back}
						onClick={() => window.history.back()}
						data-flx="app.native-titlebar.history-back"
					>
						<ArrowLeftIcon weight="bold" />
					</button>
					<button
						type="button"
						className={styles.historyButton}
						aria-label={i18n._(FORWARD_DESCRIPTOR)}
						disabled={!history.forward}
						onClick={() => window.history.forward()}
						data-flx="app.native-titlebar.history-forward"
					>
						<ArrowRightIcon weight="bold" />
					</button>
				</div>
			) : null}
			<div className={styles.center} data-flx="app.native-titlebar.center">
				<AppTitlebarContext />
			</div>
			<div className={styles.spacer} data-flx="app.native-titlebar.spacer" />
			{!isWeb && !isMacOS ? <NativeWindowControls data-flx="app.native-titlebar.controls" /> : null}
		</div>
	);
};
